import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { cleanOAuthUrl, handleOAuthCallback, isOAuthCallback } from '@/libs/oauth-utils';
import { metaPixel } from '@/libs/meta-pixel';
import { OAUTH_BRAND_SIGNUP_MARKER } from '@/libs/oauth-signup-marker';
import { ONBOARDING_PATHS } from '@/pages/onboarding/_components/onboarding-steps';
import { getMcpReturnTo } from '@/libs/mcp-return-to';
import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Reason to show after the session bootstrap failed on a freshly signed-in user.
 *
 * The tokens were just minted, so a 401 or 403 from `GET /dashboard/users/me` is
 * not a session problem: the account reaches no brand dashboard, because it has
 * no organisation, no current brand, or no brand access. Telling that user their
 * session failed and to sign in again loops them through the same refusal.
 * Anything else (server error, dropped connection) is worth retrying.
 */
function bootstrapErrorCode(error: unknown): 'no_brand_access' | 'session_failed' {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;

  return status === 401 || status === 403 ? 'no_brand_access' : 'session_failed';
}

/**
 * Where a brand that just signed up with Google goes next, read off the marker
 * its sign-up put on the redirect URL. Null for a sign-in.
 */
function signupDestination(marker: string | null): string | null {
  if (marker === OAUTH_BRAND_SIGNUP_MARKER.SELF_SIGNUP) return ONBOARDING_PATHS.brand;
  if (marker === OAUTH_BRAND_SIGNUP_MARKER.INVITATION) return '/onboarding/shopify';
  return null;
}

/**
 * OAuth Callback Handler Component
 *
 * Handles OAuth authentication callback by:
 * 1. Detecting OAuth parameters in URL (accessToken, refreshToken, type, expiresIn, error)
 * 2. Processing and storing tokens using tokenManager
 * 3. Fetching user data after successful authentication
 * 4. Cleaning sensitive data from URL
 * 5. Redirecting to dashboard or showing error
 *
 * This component should be rendered on the login and signup pages to process OAuth callbacks.
 */
export const OAuthCallbackHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshCurrentUser } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Check if this is an OAuth callback with tokens (successful OAuth)
    // Error-only parameters are handled by the page itself via searchParams
    if (!isOAuthCallback(searchParams)) {
      return;
    }

    // Prevent duplicate processing
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    console.log('[OAUTH-CALLBACK] Processing OAuth callback with tokens');

    const signupNextStep = signupDestination(searchParams.get('oauth'));
    const returnTo = getMcpReturnTo(searchParams.get('returnTo'));

    // Process the OAuth callback
    const result = handleOAuthCallback(searchParams);

    if (result.success) {
      console.log('[OAUTH-CALLBACK] OAuth callback processed successfully');

      // Clean OAuth parameters from URL
      cleanOAuthUrl();

      // Fetch user data and redirect to dashboard
      refreshCurrentUser()
        .then(() => {
          // Show success toast if this was a team invitation OAuth flow
          if (location.pathname.startsWith('/team/join')) {
            toaster.success({
              title: i18next.t('team.join.success.title'),
              description: i18next.t('team.join.success.description'),
            });
          }

          if (signupNextStep) metaPixel.track('CompleteRegistration', { content_name: 'google' });
          const destination = returnTo ?? signupNextStep ?? '/dashboard';
          console.log('[OAUTH-CALLBACK] User data loaded, redirecting to destination');
          navigate(destination, { replace: true });
        })
        .catch((error) => {
          console.error('[OAUTH-CALLBACK] Failed to load user data');
          // Redirect to login with the reason the bootstrap actually failed
          const errorCode = bootstrapErrorCode(error);
          navigate(
            returnTo
              ? `/?${new URLSearchParams({ error: errorCode, returnTo })}`
              : `/?error=${errorCode}`,
            { replace: true }
          );
        });
    } else {
      console.error('[OAUTH-CALLBACK] OAuth callback failed - invalid tokens');

      // Clean OAuth parameters from URL
      cleanOAuthUrl();

      // Redirect to login page with error
      navigate(
        returnTo
          ? `/?${new URLSearchParams({ error: 'validation_failed', returnTo })}`
          : '/?error=validation_failed',
        { replace: true }
      );
    }
  }, [searchParams, navigate, location.pathname, refreshCurrentUser, isProcessing]);

  // This component doesn't render anything
  return null;
};
